/**
 * ClientInfoSection
 *
 * Collapsible list of client-related fields to confirm before filing the claim.
 * Each item is editable and can be marked reviewed. Features inline citation pills
 * next to each field value that open the artifact inspector when clicked.
 * Emits hover/select callbacks to keep the AI explanation pane in sync.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FieldSection } from '@/components/claims-filing/common/FieldSection';
import { Button } from '@/components/ui/button';
import type {
  DataRequirement,
  DataRequirementValue,
  DataRequirementValueContext,
} from '@/services/types/data-requirements-types';

import { DisclosureSection } from '../common/DisclosureSection';
import { ReviewedFieldState } from '../hooks/useClaimFilingState';
import { type CitationSource, CitedFieldRow } from './CitedFieldRow';

/** Props for `ClientInfoSection`. */
interface ClientInfoSectionProps {
  matterId: string;
  reviewedFields: ReviewedFieldState[];
  onAccept: (key: string) => void;
  onModify: (key: string, value: string) => void;
  onHoverField?: (key: string | null) => void;
  onSelectField?: (key: string) => void;
  activeFieldKey?: string | null;
  requirementsById?: Record<string, DataRequirement>;
  onConfirmValue?: (requirementId: string, valueId: string) => void;
}

export const ClientInfoSection: React.FC<ClientInfoSectionProps> = ({
  matterId,
  reviewedFields,
  onAccept,
  onModify,
  onHoverField,
  onSelectField,
  activeFieldKey,
  requirementsById,
  onConfirmValue,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [draftValues, setDraftValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(reviewedFields.map((f) => [f.key, f.value ?? ''])),
  );
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const initialOrderRef = useRef<Map<string, number>>(new Map());
  const orderCounterRef = useRef<number>(0);

  useEffect(() => {
    const incoming: Record<string, string> = Object.fromEntries(
      reviewedFields.map((f) => [f.key, f.value ?? '']),
    );
    setDraftValues((prev) => {
      const next: Record<string, string> = { ...prev };
      Object.entries(incoming).forEach(([key, incomingVal]) => {
        if (!pendingKeys.has(key)) {
          next[key] = incomingVal;
        } else if ((prev[key] ?? '') === incomingVal) {
          next[key] = incomingVal;
          setPendingKeys((prevSet) => {
            const copy = new Set(prevSet);
            copy.delete(key);
            return copy;
          });
        }
      });
      return next;
    });
  }, [reviewedFields, pendingKeys]);

  const sortedFields = useMemo(() => {
    reviewedFields.forEach((f) => {
      if (!initialOrderRef.current.has(f.key)) {
        const { current } = orderCounterRef;
        initialOrderRef.current.set(f.key, current);
        orderCounterRef.current = current + 1;
      }
    });
    return [...reviewedFields].sort((a, b) => {
      const aSection = a.meta?.section === 'client' ? 0 : 1;
      const bSection = b.meta?.section === 'client' ? 0 : 1;
      if (aSection !== bSection) return aSection - bSection;
      const reqDiff = Number(b.required) - Number(a.required);
      if (reqDiff !== 0) return reqDiff;
      return (
        (initialOrderRef.current.get(a.key) ?? 0) -
        (initialOrderRef.current.get(b.key) ?? 0)
      );
    });
  }, [reviewedFields]);

  const handleChange = (key: string, value: string) => {
    setDraftValues((prev) => ({ ...prev, [key]: value }));
    onSelectField?.(key);
  };

  /**
   * Extract citation sources from a data requirement's values.
   * Returns an array of CitationSource objects for inline display.
   */
  const getCitationsForField = useCallback(
    (fieldKey: string): CitationSource[] => {
      if (!requirementsById) return [];
      const req = requirementsById[fieldKey];
      if (!req?.values) return [];

      const citations: CitationSource[] = [];
      const seenArtifacts = new Set<string>();

      req.values.forEach((v: DataRequirementValue) => {
        const ctx = v.context as DataRequirementValueContext | null;
        if (ctx?.artifact_id && !seenArtifacts.has(ctx.artifact_id)) {
          seenArtifacts.add(ctx.artifact_id);
          citations.push({
            artifact_id: ctx.artifact_id,
            citation_ids: ctx.citation_ids || [],
            document_name: ctx.extraction_source || undefined,
          });
        }
      });

      return citations;
    },
    [requirementsById],
  );

  /**
   * Check if a field was extracted by AI
   */
  const isFieldAIExtracted = useCallback(
    (fieldKey: string): boolean => {
      if (!requirementsById) return false;
      const req = requirementsById[fieldKey];
      if (!req?.values) return false;
      return req.values.some(
        (v: DataRequirementValue) => v.status === 'Extracted',
      );
    },
    [requirementsById],
  );

  /**
   * Get confidence score for a field (from highest confidence extracted value)
   */
  const getFieldConfidence = useCallback(
    (fieldKey: string): number | null => {
      if (!requirementsById) return null;
      const req = requirementsById[fieldKey];
      if (!req?.values) return null;

      const extractedValues = req.values.filter(
        (v: DataRequirementValue) => v.status === 'Extracted',
      );
      if (extractedValues.length === 0) return null;

      const maxConfidence = Math.max(
        ...extractedValues.map((v) => v.confidence ?? 0),
      );
      return maxConfidence > 0 ? maxConfidence : null;
    },
    [requirementsById],
  );

  const renderCandidates = (field: ReviewedFieldState) => {
    if (!requirementsById) return null;
    const req = requirementsById[field.key];
    if (!req) return null;

    const canonical =
      typeof req.canonical_value === 'string' && req.canonical_value.trim()
        ? (req.canonical_value as string)
        : null;

    const allValues = (req.values || []) as DataRequirementValue[];
    const approved = allValues
      .filter((v) => v.status === 'Approved')
      .sort((a, b) =>
        (b.modified_at || b.created_at || '').localeCompare(
          a.modified_at || a.created_at || '',
        ),
      );
    const extracted = allValues.filter((v) => v.status === 'Extracted');

    type EvidenceKind = 'canonical' | 'approved' | 'extracted';
    type CandidateItem = {
      id: string;
      value: string;
      kind: EvidenceKind;
    };
    const items: CandidateItem[] = [];
    if (canonical)
      items.push({ id: 'canonical', value: canonical, kind: 'canonical' });
    approved.forEach((v) =>
      items.push({
        id: String(v.id ?? ''),
        value: String(v.value || ''),
        kind: 'approved',
      }),
    );
    extracted.forEach((v) =>
      items.push({
        id: String(v.id ?? ''),
        value: String(v.value || ''),
        kind: 'extracted',
      }),
    );

    // Deduplicate suggestions case-insensitively and with normalized whitespace.
    const normalize = (s: string) =>
      s.trim().replace(/\s+/g, ' ').toLowerCase();
    const kindPriority: Record<EvidenceKind, number> = {
      canonical: 3,
      approved: 2,
      extracted: 1,
    };
    const mergedMap = items.reduce((acc, it: CandidateItem) => {
      const key = normalize(it.value);
      const existing = acc.get(key);
      if (!existing || kindPriority[it.kind] > kindPriority[existing.kind]) {
        // Prefer stronger evidence kind while keeping the normalized value bucketed.
        acc.set(key, { ...it });
      }
      return acc;
    }, new Map<string, CandidateItem>());
    const deduped = Array.from(mergedMap.values());

    const current = (field.value || '').trim();
    const currentNorm = normalize(current);
    const additional = deduped.filter(
      (i) => normalize(i.value) !== currentNorm,
    );
    if (additional.length === 0) return null;

    return (
      <div className="px-3 pt-1 pb-4" role="list" aria-label="Suggested values">
        <div className="text-[11px] text-gray-500 mb-1">Other suggestions</div>
        <div className="space-y-0.5">
          {additional.map((i) => (
            <div
              key={i.id}
              role="listitem"
              className="group/suggestion flex items-center justify-between py-1"
              onMouseEnter={() => onHoverField?.(field.key)}
            >
              <span className="text-sm text-gray-600 leading-5 truncate">
                {i.value}
              </span>
              <div className="opacity-0 group-hover/suggestion:opacity-100 focus-within:opacity-100 transition-opacity">
                {i.kind === 'extracted' && !canonical && (
                  <Button
                    size="sm"
                    className="text-xs"
                    onClick={async () => {
                      const btn =
                        document.activeElement as HTMLButtonElement | null;
                      if (btn) btn.disabled = true;
                      try {
                        await onConfirmValue?.(field.key, i.id);
                      } catch (e) {
                        // ignore here; parent can show toast
                      } finally {
                        if (btn) btn.disabled = false;
                      }
                    }}
                  >
                    Confirm
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DisclosureSection
      title="Client Information"
      isOpen={isOpen}
      onToggle={() => setIsOpen((v) => !v)}
    >
      <div
        className="space-y-2"
        aria-label="Client information"
        data-matter-id={matterId}
        data-active-field-key={activeFieldKey || undefined}
      >
        <FieldSection>
          {sortedFields.map((field) => (
            <div key={field.key} className="group">
              <CitedFieldRow
                id={field.key}
                label={field.displayName}
                value={draftValues[field.key] ?? ''}
                required={field.required}
                citations={getCitationsForField(field.key)}
                isSelected={activeFieldKey === field.key}
                onSelect={() => onSelectField?.(field.key)}
                isAIExtracted={isFieldAIExtracted(field.key)}
                confidence={getFieldConfidence(field.key)}
                editable
                onChange={(next: string) => handleChange(field.key, next)}
                isSaving={pendingKeys.has(field.key)}
                onSave={() => {
                  setPendingKeys((prev) => new Set(prev).add(field.key));
                  onModify(field.key, draftValues[field.key] ?? '');
                  onAccept(field.key);
                }}
              />
              {renderCandidates(field)}
            </div>
          ))}
        </FieldSection>
      </div>
    </DisclosureSection>
  );
};
